import {uploadData} from 'aws-amplify/storage';
import {getS3BucketName} from './amplifyConfig';
import {withTimeout} from './withTimeout';

/**
 * Direct-to-S3 upload for run ticket photos — see
 * docs/run-ticket-scan-spec.md for the full design writeup (per-user
 * IAM scoping, key convention, orphan-avoidance plan) and
 * lib/amplifyConfig.ts for the Storage config this depends on
 * (VITE_S3_BUCKET_NAME/VITE_S3_REGION — set for the test env as of
 * 2026-08-20, not yet for production).
 *
 * CHANGED 2026-08-21 (Duane, per his backend developer, revised same
 * day): the key used to be prefixed with the Cognito Identity Pool's
 * full identityId (a `{region}:{uuid}` string, e.g.
 * `us-east-2:472bdc2f-...`) — the backend developer said the region
 * portion isn't needed, and the colon separating it from the uuid was
 * suspected as a possible source of server-side trouble parsing/
 * handling the key. The identityId's uuid itself is KEPT (per-user
 * folder isolation still matters, see the IAM note below) — only the
 * `{region}:` prefix and its colon are stripped. Key convention is now
 * `run-tickets/{identityUuid}/{runTicketId}/photo.<ext>`, where
 * identityUuid is the identityId with any `{region}:` prefix removed.
 *
 * IMPORTANT, NOT YET CONFIRMED: the original design doc's IAM policy
 * was written to scope client upload permission using
 * `${cognito-identity.amazonaws.com:sub}` matched against the full
 * identityId path segment (per-user folder isolation). `:sub` there
 * resolves to Cognito's full `{region}:{uuid}` identityId, so if the
 * bucket policy does a literal string match against that, stripping the
 * region prefix here could make the CLIENT's own PutObject start
 * failing with Access Denied too — a regression, not a fix. Flag this
 * to the backend developer alongside the key-format change, and watch
 * the very next scan's PutObject call for a new Access Denied on the
 * client side before assuming this alone resolved anything.
 *
 * CHANGED 2026-08-22 (Duane — his backend developer needs this to
 * resolve a photo's bucket+key for his own S3 reads, e.g.
 * runTicket_AnalyzeImage): the bucket name is now prepended as the
 * FIRST path segment of whatever gets stored/sent as
 * ticketImageS3Data.imageLocation — e.g.
 * `american-warrior-test/run-tickets/{identityUuid}/{runTicketId}/photo.jpg`
 * — so the backend can split on the first "/" to know which bucket a
 * key lives in (it has no Amplify config of its own to tell it that).
 *
 * This is ONLY about that stored/GraphQL-facing string, never about how
 * the client itself puts or removes objects: the actual S3 object key
 * passed to uploadData's `path` callback below is UNCHANGED and must
 * never carry the bucket prefix — Amplify's Storage calls are already
 * scoped to the configured bucket (amplifyConfig.ts's Storage.S3.bucket),
 * so prepending the bucket name there would create a literal subfolder
 * named after the bucket INSIDE the bucket, not select a bucket.
 * uploadRunTicketPhoto below now returns the bucket-prefixed
 * imageLocation directly (the only form any caller needs); the raw S3
 * key stays private to this module (see toImageLocation).
 *
 * Symmetrically: if a stored/returned imageLocation is ever fed back
 * into an Amplify Storage call (uploadData/remove/getUrl/etc.), the
 * bucket prefix must be stripped off first — see
 * s3KeyFromImageLocation below. Nothing in this codebase does that
 * today (imageLocation only ever gets resent verbatim to the backend on
 * later edits — see runTicketMapping.ts — never fed back into Storage),
 * so this is currently unused, but any future code path that does must
 * not skip this step.
 *
 * NOT YET RECONCILED: RunTicket rows created between 2026-08-20 and
 * 2026-08-22 (test env only) have imageLocation stored WITHOUT this
 * bucket prefix. s3KeyFromImageLocation below would misparse those
 * specific old rows (it would strip "run-tickets" thinking it's the
 * bucket name) — not fixed here since nothing consumes that helper yet;
 * flag to Duane if a rescan/analyze is ever attempted against one of
 * those pre-existing rows.
 *
 * CHANGED 2026-08-22 (Duane — explicit call): the client NEVER deletes
 * S3 objects, full stop. deleteRunTicketPhotoByKey (this file's former
 * orphan-avoidance layer 2) is removed entirely. The only way a photo
 * can end up with nothing in the database pointing at it is if the
 * follow-up attach mutation never gets imageLocation to the server at
 * all — see state/runTicketPhotoSync.ts's permanent-failure branch,
 * which now only drops its own local pending-photo queue entry and
 * leaves the S3 object in place. Reclaiming those (and anything else
 * that falls through, e.g. a tab closed mid-flow) is a periodic
 * server-side job's job — removing unused photos past some age — not
 * client code. Docs/run-ticket-scan-spec.md's old "layer 3" (an S3
 * Lifecycle Expiration rule) plan is superseded by that same
 * backend-side cleanup.
 */

function extFromContentType(contentType: string): string {
   if (contentType === 'image/png') return 'png';
   if (contentType === 'image/webp') return 'webp';
   if (contentType === 'image/heic' || contentType === 'image/heif') return 'heic';
   return 'jpg'; // image/jpeg, and anything unrecognized — overwhelmingly the common case from a phone camera
}

/**
 * Cognito's identityId comes as `{region}:{uuid}` (e.g.
 * `us-east-2:472bdc2f-...`). Strips the `{region}:` prefix, keeping
 * just the uuid — see the header comment above for why.
 */
function stripRegionPrefix(identityId: string): string {
   const colonIndex = identityId.indexOf(':');
   return colonIndex === -1 ? identityId : identityId.slice(colonIndex + 1);
}

/**
 * Prepends the configured S3 bucket name as the first path segment —
 * see this file's header comment (2026-08-22 note) for why. NOT the
 * same string as the actual S3 object key, which never carries this
 * prefix.
 */
function toImageLocation(s3Key: string): string {
   const bucket = getS3BucketName();
   // Storage.S3.bucket has to be configured for uploadData (below) to
   // have succeeded at all, so bucket should always be set by the time
   // this runs — the fallback just avoids a hard crash if that
   // invariant ever breaks, rather than silently mis-tagging the
   // location with a wrong/missing bucket.
   return bucket ? `${bucket}/${s3Key}` : s3Key;
}

/**
 * Reverses toImageLocation — strips a leading `{bucket}/` segment back
 * off a stored/returned imageLocation so it's safe to hand to an
 * Amplify Storage call (uploadData/remove/getUrl), which are already
 * scoped to a bucket and must not receive one as part of the path. See
 * this file's header comment for why nothing calls this yet, and the
 * caveat about rows stored before 2026-08-22 without the prefix.
 */
export function s3KeyFromImageLocation(imageLocation: string): string {
   const bucket = getS3BucketName();
   if (bucket && imageLocation.startsWith(`${bucket}/`)) {
      return imageLocation.slice(bucket.length + 1);
   }
   const slashIndex = imageLocation.indexOf('/');
   return slashIndex === -1 ? imageLocation : imageLocation.slice(slashIndex + 1);
}

// ADDED 2026-09-10 (Duane's report: "Saving hangs" — traced to this
// specific call): every OTHER network step in the save-a-run-ticket
// flow goes through Apollo (the runTicket_CreateUpdate mutation, the
// RunTicket_AllWhere refetch, the AttachPhoto retry loop in
// state/runTicketPhotoSync.ts) and is already bounded by
// apolloClient.ts's own timeoutLink (ONLINE_REQUEST_TIMEOUT_MS,
// currently 10s) — but this raw Amplify Storage upload is NOT an Apollo
// operation, so timeoutLink never touches it, and uploadData() itself
// has no timeout of its own. On a stalled/weak field connection this
// could hang indefinitely — no error, no resolution, just a Promise
// that never settles. That hang propagated straight up through
// syncPendingRunTicketPhotoAtom's `await uploadRunTicketPhoto(...)` into
// RunTicketsPage.handleSaveTicketInner's `await
// syncAllPendingRunTicketPhotos()`, which is what keeps `savingTicket`
// true (see handleSaveTicket's try/finally) — and since this same
// day's Save-progress-indicator change also disables Cancel/the X icon
// while saving (so the user can't back out mid-save either), a hang
// here left the drawer stuck showing "Saving…" with no way out at all.
// A ticket saved with no scanned photo yet naturally shows scanStatus
// 'Pending' (see blankTicketInput/handlePhotoChange) — which is what
// made this reproducible specifically on "if scanStatus comes back
// Pending": that's exactly the shape of a ticket whose photo is still
// mid-upload during this very save.
//
// withTimeout (lib/withTimeout.ts) races this against a ceiling the
// same way apolloClient.ts's own persistencePromise wait does — 30s,
// more generous than the 10s GraphQL ceiling since this is an actual
// multi-MB image payload (a compressed phone-camera photo), not a small
// GraphQL request/response. On timeout this throws, which every
// existing caller already treats as an ordinary upload failure (see
// syncPendingRunTicketPhotoAtom's own catch above it — leaves the
// pending photo queued in IndexedDB to retry later, exactly like any
// other upload failure) — nothing else had to change to handle this
// case. withTimeout does NOT cancel the underlying uploadData() call —
// there's no cancellation API wired up here — so a photo that
// eventually does finish uploading after the 30s ceiling trips still
// lands in S3 at its deterministic key; it just won't be attached by
// THIS save's own attempt, only by a later retry (same as any other
// timed-out-but-still-in-flight upload).
const UPLOAD_TIMEOUT_MS = 30000;

/**
 * Uploads the photo and returns the bucket-prefixed value to store as
 * ticketImageS3Data.imageLocation (see lib/runTicketMapping.ts and this
 * file's header comment) — NOT the same string as the raw S3 object key
 * Amplify Storage itself used for the PutObject call.
 */
export async function uploadRunTicketPhoto(runTicketId: string, blob: Blob, contentType: string): Promise<string> {
   const ext = extFromContentType(contentType);
   const result = await withTimeout(
      uploadData({
         // identityId is typed optional by @aws-amplify/storage (it's
         // whatever fetchAuthSession() resolved at call time) — pre-existing
         // gap, unrelated to today's changes, just never caught before
         // because tsc's incremental build hadn't re-checked this file until
         // this edit touched it. Throwing here (rather than silently
         // uploading to a `run-tickets//...` path with an empty user
         // segment) turns a missing identityId into an immediate, legible
         // error instead of a confusing downstream Access Denied from the
         // per-user-scoped IAM policy.
         path: ({identityId}) => {
            if (!identityId) throw new Error('No Cognito identityId available — is the user actually signed in?');
            return `run-tickets/${stripRegionPrefix(identityId)}/${runTicketId}/photo.${ext}`;
         },
         data: blob,
         options: {contentType},
      }).result,
      UPLOAD_TIMEOUT_MS,
      () => console.warn(`Run ticket photo upload for ${runTicketId} did not finish within ${UPLOAD_TIMEOUT_MS}ms`),
   );
   if (!result) {
      throw new Error(`Run ticket photo upload for ${runTicketId} timed out after ${UPLOAD_TIMEOUT_MS}ms`);
   }
   return toImageLocation(result.path);
}
